import { z } from 'zod';
import { Confidence } from './company-enrichment.js';
/**
 * Request + response for `POST /api/onboarding/enrich-person` —
 * Gemini-grounded web search over name + email (+ optional linkedinUrl /
 * companyName disambiguators). Each field carries `Confidence`; the
 * wizard skips `unknown` rows. Fields are nullable (not omitted) so a
 * partial profile renders manual entry on the null slots without
 * crashing.
 */
const EnrichedField = (value) => z.object({
    value,
    confidence: Confidence,
});
// Public profile route only — apex + regional subdomains, `/in/<slug>`
// path. Rejects company pages / feed posts / job links so a non-profile
// URL doesn't pollute the disambiguator.
const LINKEDIN_PROFILE_URL = /^https?:\/\/([a-z]+\.)?linkedin\.com\/in\/[^/?#]+/i;
export const EnrichPersonRequest = z.object({
    /** Full name (typically from Firebase auth `displayName`). Required —
     *  the web search is built around the person's name + email. */
    name: z.string().trim().min(1).max(200),
    /** Email address (typically from Firebase auth `email`). Required —
     *  domain hints at the employer; the full address disambiguates
     *  same-name people. */
    email: z.string().email().max(320),
    /** Optional public LinkedIn profile URL. When provided, fed to the
     *  search as a disambiguator and stored on the user record as a
     *  passive profile link. Validates `linkedin.com/in/<slug>` so a
     *  company page or X/Twitter paste doesn't sneak through. */
    linkedinUrl: z
        .string()
        .url()
        .regex(LINKEDIN_PROFILE_URL, {
        message: 'must be a public LinkedIn profile URL (linkedin.com/in/<slug>)',
    })
        .optional(),
    /** Optional company name to disambiguate the search ("John Smith at
     *  Acme" vs. "John Smith at Beta"). The wizard passes this once the
     *  user has typed it on Step 1; otherwise omitted. */
    companyName: z.string().trim().max(200).optional(),
});
// Single-vendor today — kept as an enum so a future grounding-source
// swap is a type-checked change.
export const PersonEnrichmentProvider = z.enum(['gemini-web-search']);
export const EnrichPersonResponse = z.object({
    /** Display name as the web search resolved it (often matches the
     *  request name but can correct casing or include a middle name). */
    fullName: EnrichedField(z.string().max(200)).nullable(),
    /** Current title / occupation — feeds BusinessContext.userRole. */
    title: EnrichedField(z.string().max(200)).nullable(),
    /** Profile summary / bio paragraph — feeds userBackgroundSummary. */
    background: EnrichedField(z.string().max(2000)).nullable(),
    /** Current company name. Cross-check against the typed
     *  `companyName` and warn on mismatch. */
    currentCompany: EnrichedField(z.string().max(200)).nullable(),
    /** Current company website if the search surfaced one. */
    currentCompanyWebsite: EnrichedField(z.string().max(500)).nullable(),
    /** "5 years 2 months" — surfaces in the AgentBubble narration. */
    tenure: EnrichedField(z.string().max(120)).nullable(),
    /** Past role headlines (most-recent first, up to 5). Context for
     *  the welcome-note prompt. */
    pastRoles: EnrichedField(z.array(z.string().max(300)).max(6)).nullable(),
    /** Country / region. Useful fallback when company-enrichment
     *  doesn't surface a region. */
    location: EnrichedField(z.string().max(200)).nullable(),
    /** Which provider answered. Logged to audit so the cost dashboard
     *  + future provider swaps stay observable. */
    provider: PersonEnrichmentProvider,
    /** Free-form summary, ~1 short paragraph, of what we know about
     *  this person — fed to the welcome-note prompt. */
    summary: z.string().max(2000),
    /** Public web sources Gemini grounded against. Each URL is a
     *  citation chunk from `groundingMetadata.groundingChunks`. */
    citations: z
        .array(z.object({
        url: z.string().url(),
        title: z.string().max(300).optional(),
    }))
        .max(20)
        .default([]),
});
